import React, { useState, useEffect, useMemo } from 'react';
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { StyledForm, VaccinationOptions } from './style';
import { Button, Checkbox, Input, Radio, RadioChangeEvent, notification } from 'antd';
import { useUpdateProfessionalClientVaccinations } from '../../../hooks/professionals/useClientGeneralInfo';
import { useUpdateClientVaccinations } from '../../../hooks/client/useClientGeneralInfo';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import useListOptionsForAllergiesAndOthers from '../../../hooks/common/useListOptions';
import CustomLoading from '../../Common/Loading';
import { useGetProfessionalClient } from '../../../hooks/professionals/useProfessionalClient';
import { NotificationTitle } from '../../Common/Notification/style';
import { CheckCircleOutlined } from '@ant-design/icons';
import { theme } from '../../../styles/theme';


const VaccinationsForm = () => {
  const [api, contextHolder] = notification.useNotification();
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const { user } = useSelector((state: RootState) => state.auth);
  const isClient = !!user && 'professionalCode' in user;
  const [form] = StyledForm.useForm();
  const { data: listOptions, loading: loadingListOptions } = useListOptionsForAllergiesAndOthers();
  const { update: updateProfessionalClientVaccination, loading: loadingProfessional } = useUpdateProfessionalClientVaccinations();
  const { update: updateClientVaccination, loading: loadingClient } = useUpdateClientVaccinations();
  const loadingUpdateProfessionalClientVaccination = isClient ? loadingClient : loadingProfessional;
  const { refetch: refetchClient } = useGetProfessionalClient(selectedClient?.id as string);
  const [withVaccinations, setWithVaccinations] = useState<boolean>(Boolean(selectedClient?.vaccinations));
  const [selectedVaccinations, setSelectedVaccinations] = useState<Record<string, boolean>>({});

  const vaccinationsOptions = useMemo(() => {
    if (listOptions) {
      return listOptions?.vaccinations?.map((option: any) => ({
        value: option.id,
        label: option.name
      }));
    }
  }, [listOptions]);

  console.log('listOptions', listOptions)
  const vaccinations = useMemo(() => (selectedClient?.vaccinations || []), [selectedClient]);

  useEffect(() => {
    const initialFormValues = vaccinations.reduce((acc: any, vaccination) => {
      const vaccinationId = vaccination.vaccination?.id;
      acc[vaccinationId] = {
        when: vaccination.when,
        physiologicalResponse: vaccination.physiologicalResponse,
        otherName: vaccination.otherName,
      };
      return acc;
    }, {});

    form.setFieldsValue(initialFormValues);
    setSelectedVaccinations(
      vaccinations.reduce((acc: any, vaccination) => {
        acc[vaccination.vaccination.id] = true;
        return acc;
      }, {})
    );
  }, [form, vaccinations]);

  const onFinish = async (values: any) => {
    const paramsToSend = (vaccinationsOptions || [])
      .filter((opt: any) => selectedVaccinations[opt.value])
      .map((opt: any) => {
        const saved = vaccinations.find(
          (v: any) => String(v.vaccination?.id) === String(opt.value)
        );

        // id is ID! in the schema.
        // For existing DB records pass the real id (update).
        // For new records, we send an empty string '' because it's a non-nullable field.
        // The backend should interpret an empty string as "not an existing record".
        const idValue = (saved?.id != null && saved.id !== '' && String(saved.id) !== '0') ? String(saved.id) : '';

        const rawParam: any = {
          optionVaccinationId: String(opt.value),
          when: values[opt.value]?.when ?? '',
          otherName: values[opt.value]?.otherName ?? '',
          physiologicalResponse: values[opt.value]?.physiologicalResponse ?? '',
          id: idValue,
        };

        return rawParam;
      });

    console.log('Vaccinations payload:', JSON.stringify(paramsToSend));

    try {
      let res;
      if (isClient) {
        res = await updateClientVaccination(paramsToSend);
      } else {
        res = await updateProfessionalClientVaccination(selectedClient?.id as string, paramsToSend);
      }

      const success = isClient ? res?.clientUpdateVaccinations : res?.professionalClientUpdateVaccinations;

      if (success) {
        await refetchClient();
        api.info({
          message: <NotificationTitle>Vaccinations updated successfully</NotificationTitle>,
          placement: 'topRight',
          icon: <CheckCircleOutlined style={{ color: theme.color.primary }} />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex',
          },
        });
      } else {
        throw new Error('Save operation was not successful');
      }
    } catch (error) {
      console.error('Error saving vaccinations:', error);
      api.error({
        message: <NotificationTitle>Error saving vaccinations</NotificationTitle>,
        description: 'There was an issue saving the vaccination details. Please try again.',
        placement: 'topRight',
        style: {
          background: `${theme.bg.secondary}`,
          borderRadius: 10,
          alignItems: 'center',
          display: 'flex',
        },
      });
    }
  };

  const onChangeWithVaccination = ({ target: { value } }: RadioChangeEvent) => {
    setWithVaccinations(value);
  };

  const handleVaccinationSelection = (vaccinationId: string, checked: boolean) => {
    setSelectedVaccinations((prev) => ({
      ...prev,
      [vaccinationId]: checked,
    }));
  };

  if (loadingListOptions) {
    return <CustomLoading />
  }

  return (
    <>
      {contextHolder}
      <StyledForm
        {..._FORM_ITEM_LAYOUT}
        form={form}
        name="vaccinations_form"
        onFinish={onFinish}
        initialValues={{}} // No initialValues needed as we're setting them in useEffect
        style={{ marginTop: '1rem' }}
        scrollToFirstError
      >
        <p>I have had vaccinations in the past</p>
        <Radio.Group
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          onChange={onChangeWithVaccination}
          value={withVaccinations}
          optionType="button"
          buttonStyle="solid"
        />
        {withVaccinations && (
          <VaccinationOptions>
            {vaccinationsOptions.map((vaccination: any) => (
              <div key={vaccination.value} style={{ marginBottom: '1rem' }}>
                <Checkbox
                  checked={!!selectedVaccinations[vaccination.value]}
                  onChange={(e) => handleVaccinationSelection(vaccination.value, e.target.checked)}
                  style={{ marginBottom: '0.5rem' }}
                >
                  {vaccination.label}
                </Checkbox>
                {selectedVaccinations[vaccination.value] && (
                  <div>
                    <StyledForm.Item
                      name={[vaccination.value, 'when']}
                      label="When?"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify when you got this vaccination',
                        },
                      ]}
                    >
                      <Input />
                    </StyledForm.Item>
                    <StyledForm.Item
                      name={[vaccination.value, 'physiologicalResponse']}
                      label="Did you have any specific responses?"
                    >
                      <Input />
                    </StyledForm.Item>

                    {vaccination.label === 'Other' && (
                      <StyledForm.Item
                        name={[vaccination.value, 'otherName']}
                        label="Which vaccination?"
                        rules={[
                          {
                            required: true,
                            message: 'Please specify the vaccination name',
                          },
                        ]}
                      >
                        <Input />
                      </StyledForm.Item>
                    )}
                  </div>
                )}
              </div>
            ))}
          </VaccinationOptions>
        )}

        <StyledForm.Item {..._TAIL_FORM_ITEM_LAYOUT}>
          <Button loading={loadingUpdateProfessionalClientVaccination} type="primary" htmlType="submit">
            Save
          </Button>
        </StyledForm.Item>
      </StyledForm>
    </>
  );
};

export default VaccinationsForm;
